import { useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '@/store/hooks';

import {
  billingApi,
  useCheckPaymentStatusQuery,
  useCreateCustomerPortalSessionMutation,
  useGetApiUsageQuery,
  useGetCreditBurnQuery,
  useGetCreditPlansQuery,
  useGetMyCreditsQuery,
  useGetTransactionsQuery,
  useStartPaymentSessionMutation,
} from './store/api';
import type { TransactionQueryParams } from './types';

export function useApiUsage() {
  return useGetApiUsageQuery(undefined, { refetchOnMountOrArgChange: true });
}

export function useCreditBurn(days = 30) {
  return useGetCreditBurnQuery(days);
}

export function useMyCredits() {
  return useGetMyCreditsQuery(undefined, { refetchOnMountOrArgChange: true });
}

export function useCreditPlans() {
  return useGetCreditPlansQuery();
}

export function useTransactions(params: TransactionQueryParams) {
  return useGetTransactionsQuery(params);
}

export function useStartPaymentSession() {
  return useStartPaymentSessionMutation();
}

export function useCreateCustomerPortalSession() {
  return useCreateCustomerPortalSessionMutation();
}

export function useCheckPaymentStatus(sessionId: string | null) {
  return useCheckPaymentStatusQuery(sessionId ?? '', { skip: !sessionId });
}

export type PostPaymentCheckResult = {
  isChecking: boolean;
  isSuccess: boolean;
  isFailed: boolean;
  planName?: string;
};

const MAX_POLLS = 10;

// Polls checkPaymentStatus after Stripe redirects back with ?session_id.
// Starts as soon as sessionId is non-null — no ?status URL param required.
// Stops on success, non-pending status, query error, or after MAX_POLLS attempts.
export function usePostPaymentCheck(sessionId: string | null): PostPaymentCheckResult {
  const dispatch = useAppDispatch();
  const [resolved, setResolved] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [planName, setPlanName] = useState<string | undefined>(undefined);
  const pollCount = useRef(0);

  const { data, error } = useCheckPaymentStatusQuery(sessionId ?? '', {
    skip: !sessionId || resolved,
    pollingInterval: resolved ? 0 : 3000,
  });

  useEffect(() => {
    if (!data) return;
    if (data.status === 'success') {
      setResolved(true);
      setSucceeded(true);
      setPlanName(data.credit_plan_name ?? undefined);
      dispatch(billingApi.util.invalidateTags(['Billing', 'Transaction']));
    } else if (data.status !== 'pending') {
      setResolved(true);
    } else {
      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) setResolved(true);
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (error) setResolved(true);
  }, [error]);

  return {
    isChecking: !!sessionId && !resolved,
    isSuccess: succeeded,
    isFailed: resolved && !succeeded && !!sessionId,
    planName,
  };
}
